import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  useTheme,
  IconButton,
  InputAdornment,
  alpha,
} from "@mui/material";
import { useState, useCallback, memo, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const MODES = [
  { value: "UPI", label: "UPI (GPay, PhonePe)" },
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "Bank Transfer", label: "Bank Transfer" },
];

function AddPaymentModal({ open, onClose, onSave }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("UPI");
  const [dateTime, setDateTime] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    if (!open) {
      setAmount("");
      setMode("UPI");
      setDateTime("");
      setFile(null);
      setSaving(false);
    }
  }, [open]);

  const handleSave = useCallback(async () => {
    if (!amount || saving) return;

    setSaving(true);

    const fd = new FormData();
    fd.append("amount", amount);
    fd.append("mode", mode);
    if (dateTime) fd.append("created_at", new Date(dateTime).toISOString());
    if (file) fd.append("receipt", file);

    try {
      await onSave(fd);
      onClose();
    } finally {
      setSaving(false);
    }
  }, [amount, mode, dateTime, file, saving, onSave, onClose]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          width: "100%",
          maxWidth: 520,
          m: 2,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            Add Payment
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#94a3b8",
              width: 32,
              height: 32,
              "&:hover": {
                color: "#64748b",
                bgcolor: "#f1f5f9"
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Form */}
        <Box sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            {/* Amount Field */}
            <Box>
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", mb: 1 }}
              >
                Amount (₹)
              </Typography>
              <TextField
                placeholder="0.00"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: "#94a3b8", fontWeight: 600, fontSize: 15 }}>₹</Typography>
                    </InputAdornment>
                  ),
                  sx: { fontSize: 15 }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    "& fieldset": { border: "none" },
                    "&:hover": {
                      bgcolor: "#f1f5f9",
                      borderColor: "#cbd5e1"
                    },
                    "&.Mui-focused": {
                      bgcolor: "#ffffff",
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                    },
                  },
                }}
              />
            </Box>

            {/* Payment Mode and Date/Time Row */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", mb: 1 }}
                >
                  Payment Mode
                </Typography>
                <TextField
                  select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  fullWidth
                  InputProps={{
                    sx: { fontSize: 14 }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      "& fieldset": { border: "none" },
                      "&:hover": {
                        bgcolor: "#f1f5f9",
                        borderColor: "#cbd5e1"
                      },
                      "&.Mui-focused": {
                        bgcolor: "#ffffff",
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                    },
                  }}
                >
                  {MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", mb: 1 }}
                >
                  Date & Time
                </Typography>
                <TextField
                  type="datetime-local"
                  value={dateTime}
                  onChange={(e) => setDateTime(e.target.value)}
                  fullWidth
                  InputProps={{
                    sx: { fontSize: 14 }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      "& fieldset": { border: "none" },
                      "&:hover": {
                        bgcolor: "#f1f5f9",
                        borderColor: "#cbd5e1"
                      },
                      "&.Mui-focused": {
                        bgcolor: "#ffffff",
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Upload Receipt */}
            <Box>
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", mb: 1 }}
              >
                Upload Receipt
              </Typography>
              <Box
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                  border: dragActive
                    ? `2.5px dashed ${theme.palette.primary.main}`
                    : `2.5px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                  borderRadius: 2.5,
                  p: 4,
                  textAlign: "center",
                  bgcolor: dragActive ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.02),
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxSizing: "border-box",
                  display: "block",
                  width: "100%",
                  "&:hover": {
                    border: `2.5px dashed ${theme.palette.primary.main}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
                component="label"
              >
                <input
                  hidden
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
                {file ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: "#10b981", fontSize: 28 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                      {file.name}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <CloudUploadIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", mb: 0.5 }}>
                      Click to upload or drag and drop
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: theme.palette.primary.main, fontWeight: 500 }}>
                      PNG, JPG or PDF (max. 5MB)
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
              <Button
                onClick={onClose}
                disabled={saving}
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                    color: "#475569"
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!amount || saving}
                onClick={handleSave}
                startIcon={<CheckCircleOutlineIcon />}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: 14,
                  px: 3.5,
                  py: 1,
                  borderRadius: 3,
                  textTransform: "none",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#cbd5e1",
                    color: "#94a3b8",
                  },
                }}
              >
                {saving ? "Saving..." : "Save Payment"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default memo(AddPaymentModal);
